export enum LocalStorageKey {
  HasMutedTTS = "hasMutedTTS",
  VideoVolume = "videoVolume",
  VideoIsMuted = "videoIsMuted",
  VideoPlaybackRate = "videoPlaybackRate",
  VideoPlayerVisible = "videoPlayerVisible",
  LandingPageProject = "landing_page_project",
  UnverifiedEmail = "unverifiedEmail",
  CurrentFolderId = "currentFolderId",
  SelectedReportIdsBySurveyId = "selectedReportIdsBySurveyId",
  QuestionAnalysisResponseFilters = "questionAnalysisResponseFilters",
  ShowEmotionOnResponses = "showEmotionOnResponses",
  PreviewLlmSelection = "previewLlmSelection",
  ShowPreviewLlmOverride = "showPreviewLlmOverride",
  LastAuthMethod = "lastAuthMethod",
  WhatsNewSeenVersion = "whatsNewSeenVersion",
  ConjointTargetNByItem = "conjointTargetNByItem",
  UnifiedChatDraftTexts = "unifiedChatDraftTexts",
  UnifiedChatActiveChatIds = "unifiedChatActiveChatIds",
  HiddenGuideLeaveWarnings = "hiddenGuideLeaveWarnings",
}

export type StoredLastAuthMethod =
  | "google"
  | "microsoft"
  | "email"
  | "saml_sso";

// Mirrors PreviewLlmSelection in components/interview/types without importing it.
// model/promptVersion are kept on non-custom modes so Custom can restore the last pin.
export type StoredPreviewLlmSelection = {
  mode: "default" | "custom" | "variant";
  variantKey?: string;
  model?: string;
  promptVersion?: number;
};

export type LocalStorageSchema = {
  [LocalStorageKey.HasMutedTTS]: boolean;
  [LocalStorageKey.VideoVolume]: number;
  [LocalStorageKey.VideoIsMuted]: boolean;
  [LocalStorageKey.VideoPlaybackRate]: number;
  [LocalStorageKey.VideoPlayerVisible]: boolean;
  [LocalStorageKey.LandingPageProject]: string | null;
  [LocalStorageKey.UnverifiedEmail]: string | null;
  [LocalStorageKey.CurrentFolderId]: string | null;
  // Written by zustand persist (JSON envelope), like CurrentFolderId.
  [LocalStorageKey.SelectedReportIdsBySurveyId]: string | null;
  [LocalStorageKey.QuestionAnalysisResponseFilters]: string | null;
  [LocalStorageKey.ShowEmotionOnResponses]: string | null;
  [LocalStorageKey.PreviewLlmSelection]: StoredPreviewLlmSelection;
  [LocalStorageKey.ShowPreviewLlmOverride]: boolean;
  [LocalStorageKey.LastAuthMethod]: StoredLastAuthMethod | null;
  [LocalStorageKey.WhatsNewSeenVersion]: string | null;
  [LocalStorageKey.ConjointTargetNByItem]: Record<string, number>;
  // Unsent composer text by chat id, oldest first.
  [LocalStorageKey.UnifiedChatDraftTexts]: Record<string, string>;
  // Active chat id by org id.
  [LocalStorageKey.UnifiedChatActiveChatIds]: Record<string, string>;
  // When each user hid the guide leave warning, by `${userId}:${surveyId}`.
  [LocalStorageKey.HiddenGuideLeaveWarnings]: Record<string, string>;
};

interface LocalStorageConfig<T> {
  serializer: (value: T) => string;
  deserializer: (value: string) => T;
}

function serializeBoolean(value: boolean) {
  return value ? "true" : "false";
}

function deserializeBoolean(value: string) {
  return value === "true";
}

function serializeNumber(value: number) {
  return value.toString();
}

function deserializeNumber(value: string) {
  return parseFloat(value);
}

function serializeString(value: string | null) {
  return value ?? "";
}

function deserializeString(value: string) {
  if (value === "") {
    return null;
  }
  return value;
}

// Planned sample sizes for the conjoint design check, keyed by question.
const deserializeTargetNByItem = (value: string): Record<string, number> => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null) return {};
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] =>
          typeof entry[1] === "number" &&
          Number.isFinite(entry[1]) &&
          entry[1] > 0
      )
    );
  } catch {
    return {};
  }
};

const DEFAULT_PREVIEW_LLM_SELECTION: StoredPreviewLlmSelection = {
  mode: "default",
};

const deserializePreviewLlmSelection = (
  value: string
): StoredPreviewLlmSelection => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "mode" in parsed &&
      (parsed.mode === "default" ||
        parsed.mode === "custom" ||
        parsed.mode === "variant")
    ) {
      return parsed as StoredPreviewLlmSelection;
    }
  } catch {
    // fall through
  }
  return DEFAULT_PREVIEW_LLM_SELECTION;
};

const deserializeStringRecord = (value: string): Record<string, string> => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string"
      )
    );
  } catch {
    return {};
  }
};

export const LOCAL_STORAGE_CONFIG: {
  [K in LocalStorageKey]: LocalStorageConfig<LocalStorageSchema[K]>;
} = {
  [LocalStorageKey.HasMutedTTS]: {
    serializer: serializeBoolean,
    deserializer: deserializeBoolean,
  },
  [LocalStorageKey.VideoVolume]: {
    serializer: serializeNumber,
    deserializer: deserializeNumber,
  },
  [LocalStorageKey.VideoIsMuted]: {
    serializer: serializeBoolean,
    deserializer: deserializeBoolean,
  },
  [LocalStorageKey.VideoPlaybackRate]: {
    serializer: serializeNumber,
    deserializer: deserializeNumber,
  },
  [LocalStorageKey.VideoPlayerVisible]: {
    serializer: serializeBoolean,
    deserializer: deserializeBoolean,
  },
  [LocalStorageKey.LandingPageProject]: {
    serializer: serializeString,
    deserializer: deserializeString,
  },
  [LocalStorageKey.UnverifiedEmail]: {
    serializer: serializeString,
    deserializer: deserializeString,
  },
  [LocalStorageKey.CurrentFolderId]: {
    serializer: serializeString,
    deserializer: deserializeString,
  },
  [LocalStorageKey.SelectedReportIdsBySurveyId]: {
    serializer: serializeString,
    deserializer: deserializeString,
  },
  [LocalStorageKey.QuestionAnalysisResponseFilters]: {
    serializer: serializeString,
    deserializer: deserializeString,
  },
  [LocalStorageKey.ShowEmotionOnResponses]: {
    serializer: serializeString,
    deserializer: deserializeString,
  },
  [LocalStorageKey.PreviewLlmSelection]: {
    serializer: (value) => JSON.stringify(value),
    deserializer: deserializePreviewLlmSelection,
  },
  [LocalStorageKey.WhatsNewSeenVersion]: {
    serializer: serializeString,
    deserializer: deserializeString,
  },
  [LocalStorageKey.ShowPreviewLlmOverride]: {
    serializer: serializeBoolean,
    deserializer: deserializeBoolean,
  },
  [LocalStorageKey.ConjointTargetNByItem]: {
    serializer: (value) => JSON.stringify(value),
    deserializer: deserializeTargetNByItem,
  },
  [LocalStorageKey.UnifiedChatDraftTexts]: {
    serializer: (value) => JSON.stringify(value),
    deserializer: deserializeStringRecord,
  },
  [LocalStorageKey.UnifiedChatActiveChatIds]: {
    serializer: (value) => JSON.stringify(value),
    deserializer: deserializeStringRecord,
  },
  [LocalStorageKey.HiddenGuideLeaveWarnings]: {
    serializer: (value) => JSON.stringify(value),
    deserializer: deserializeStringRecord,
  },
  [LocalStorageKey.LastAuthMethod]: {
    serializer: (value) => value ?? "",
    deserializer: (value) =>
      value === "google" ||
      value === "microsoft" ||
      value === "email" ||
      value === "saml_sso"
        ? value
        : null,
  },
};

export function hasLocalStorage(key: LocalStorageKey): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    return window.localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

export function readLocalStorage<K extends LocalStorageKey>(
  key: K,
  defaultValue: LocalStorageSchema[K]
): LocalStorageSchema[K] {
  if (typeof window === "undefined") {
    return defaultValue;
  }
  try {
    const stored = window.localStorage.getItem(key);
    if (stored === null) {
      return defaultValue;
    }
    return LOCAL_STORAGE_CONFIG[key].deserializer(stored);
  } catch {
    return defaultValue;
  }
}

export function writeLocalStorage<K extends LocalStorageKey>(
  key: K,
  value: LocalStorageSchema[K]
): void {
  try {
    const serialized = LOCAL_STORAGE_CONFIG[key].serializer(value);
    window.localStorage.setItem(key, serialized);
  } catch {
    console.warn(`Failed to write localStorage item ${key}`);
  }
}
